import { ClassSpecial } from '@op/constants/common';
import React, { useMemo } from 'react';

type CricleCommonType = {
  x: number;
  y: number;
  r: number;
};

export default function BrandHealthCharts({
  brandHealth = 0,
  totalBrandHealth = 100,
  bgColor = '#F5F5F8',
  negativeColor = '#F17C86',
  positiveColor = '#69D19C',
  centerColor = '#6E7794',
  strokeWidth = 16,
  width = 100,
  height = 50,
}: {
  brandHealth: number;
  totalBrandHealth?: number;
  bgColor?: string;
  negativeColor?: string;
  positiveColor?: string;
  centerColor?: string;
  strokeWidth?: number;
  width?: number;
  height?: number;
}) {
  const lineColor = useMemo(() => {
    if (brandHealth === 0) return centerColor;
    if (brandHealth > 0) return positiveColor;
    return negativeColor;
  }, [brandHealth]);

  const proportion = useMemo(
    () => brandHealth / totalBrandHealth,
    [brandHealth, totalBrandHealth],
  );

  const getCircleCommon = (w: number, h: number, strokeWidth: number) => {
    const half = strokeWidth / 2;
    const hR = h - half;
    const wR = (w - half) / 2;
    const r = Math.min(hR, wR) - 4;

    const x = w / 2;
    const y = h + half;
    return {
      x,
      y,
      r,
    };
  };

  const getStartEndPointer = ({ x, y, r }: CricleCommonType) => {
    const beginPointer = { x: x - r, y };
    const endPointer = { x: x + r, y };

    return {
      beginPointer,
      endPointer,
    };
  };

  const { d, x, y, r, beginPointer } = useMemo(() => {
    const { x, y, r } = getCircleCommon(width, height, strokeWidth);
    const { beginPointer, endPointer } = getStartEndPointer({ x, y, r });
    const d = `M ${beginPointer.x} ${beginPointer.y} A ${r} ${r} 0 0 1 ${endPointer.x} ${endPointer.y}`;
    return {
      d,
      x,
      y,
      r,
      beginPointer,
    };
  }, [width, height, strokeWidth]);

  // 计算斜线的相关参数
  const { from, to, strokeDash, ...lineBrandHealth } = useMemo(() => {
    const center = `${x} ${y}`;
    const deg = 90; // 90 + 90 * proportion;

    return {
      x1: beginPointer.x - strokeWidth / 2 - 2,
      y1: beginPointer.y,
      x2: x,
      y2: y,
      transform: `rotate(${deg} ${center})`,
      from: `${90} ${center}`,
      to: `${deg} ${center}`,
      strokeDash: strokeWidth + 4,
    };
  }, [x, y, beginPointer, proportion]);

  function calculateRightTriangleSides({ x, y, r }: any, theta: number) {
    // 斜边长度
    const hypotenuse = r;

    // 角度（弧度制）
    const angle = Math.PI / (180 / theta);

    // 计算直角边长度
    const oppositeSide = hypotenuse * Math.sin(angle);
    const adjacentSide = hypotenuse * Math.cos(angle);

    return `M ${x} ${y - r} A ${r} ${r} 0 0 1 ${x + oppositeSide} ${
      y - adjacentSide
    }`;
  }

  function calculateLeftTriangleSides(
    { x, y, r }: CricleCommonType,
    theta: number,
  ) {
    // 斜边长度
    const hypotenuse = r;

    // 角度（弧度制）
    const angle = Math.PI / (180 / theta);

    // 计算直角边长度
    const oppositeSide = hypotenuse * Math.sin(angle);
    const adjacentSide = hypotenuse * Math.cos(angle);

    return `M ${x} ${y - r} A ${r} ${r} 0 0 0 ${x - adjacentSide} ${
      y - oppositeSide
    }`;
  }

  const { dStroke, strokeDasharray, formStrokeDasharray } = useMemo(() => {
    const item = getCircleCommon(width, height, strokeWidth);
    const proportion = brandHealth / totalBrandHealth;
    const deg = 90 + 90 * proportion;
    const func =
      brandHealth > 0
        ? calculateRightTriangleSides
        : calculateLeftTriangleSides;
    const d = func(item, deg);
    const dashAllWidth = Math.ceil(Math.PI * r);
    const dashWidth = Math.abs(Math.ceil(Math.PI * r * 0.5 * proportion));

    return {
      dStroke: d,
      strokeDasharray: `${dashWidth} ${dashAllWidth}`,
      formStrokeDasharray: `${0} ${dashAllWidth}`,
    };
  }, [width, height, strokeWidth, proportion]);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      version="1.1"
      width={width} 
      className={ClassSpecial.NO_FILTER}
      height={height + 10 + strokeWidth / 2}
    >
      <defs>
        <linearGradient id="halfGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="50%" stopColor={negativeColor} stopOpacity={0.1} />
          <stop offset="50%" stopColor={positiveColor} stopOpacity={0.1} />
        </linearGradient>
      </defs>
      <path
        d={d}
        stroke={'url(#halfGradient)'}
        strokeWidth={strokeWidth}
        fill="none"
        vectorEffect="non-scaling-stroke"
      />
      <path
        d={dStroke}
        stroke={lineColor}
        strokeWidth={strokeWidth}
        strokeDasharray={strokeDasharray}
        fill="none"
        vectorEffect="non-scaling-stroke"
      >
        <animate
          attributeType="XML"
          attributeName="stroke-dasharray"
          from={formStrokeDasharray}
          to={strokeDasharray}
          dur="1s"
          calcMode="paced"
          repeatCount="1"
        />
      </path>

      <line
        {...lineBrandHealth}
        strokeDasharray={`${strokeDash} ${r}`}
        stroke={lineColor}
        strokeWidth="2"
        strokeLinecap="round" 
      >
        <animateTransform
          attributeName="transform"
          type="rotate"
          from={from}
          to={to}
          dur="1s"
          calcMode="paced"
          repeatCount="1"
        />
      </line>
      <text
        style={{
          fontSize: 20,
          fontWeight: 700,
          fontFamily: 'num-b',
        }}
        x={x}
        textAnchor="middle"
        className={ClassSpecial.NO_FILTER}
        y={y + 5}
      >
        {brandHealth}
      </text>
    </svg>
  );
}
